import { createContext, createElement, isValidElement, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Fragment, jsxDEV } from "react/jsx-dev-runtime";
import { GeoJSONSource, Map as Map$2, Marker, Popup } from "maplibre-gl";
import { createRoot } from "react-dom/client";
import "maplibre-gl/dist/maplibre-gl.css";
//#region \0rolldown/runtime.js
var __create = Object.create;
var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __getOwnPropNames = Object.getOwnPropertyNames;
var __getProtoOf = Object.getPrototypeOf;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __commonJSMin = (cb, mod) => () => (mod || (cb((mod = { exports: {} }).exports, mod), cb = null), mod.exports);
var __copyProps = (to, from, except, desc) => {
	if (from && typeof from === "object" || typeof from === "function") for (var keys = __getOwnPropNames(from), i = 0, n = keys.length, key; i < n; i++) {
		key = keys[i];
		if (!__hasOwnProp.call(to, key) && key !== except) __defProp(to, key, {
			get: ((k) => from[k]).bind(null, key),
			enumerable: !(desc = __getOwnPropDesc(from, key)) || desc.enumerable
		});
	}
	return to;
};
var __toESM = (mod, isNodeMode, target) => (target = mod != null ? __create(__getProtoOf(mod)) : {}, __copyProps(isNodeMode || !mod || !mod.__esModule ? __defProp(target, "default", {
	value: mod,
	enumerable: true
}) : target, mod));
//#endregion
//#region src/context/Map.context.tsx
var _jsxFileName$8 = "/home/forge/apps.dev.qubictron.com/packages/maplib/src/context/Map.context.tsx";
var MapContext = createContext(null);
MapContext.displayName = "MapContext";
var MapProvider = ({ value, children }) => {
	return /* @__PURE__ */ jsxDEV(MapContext.Provider, {
		value,
		children
	}, void 0, false, {
		fileName: _jsxFileName$8,
		lineNumber: 16,
		columnNumber: 10
	}, void 0);
};
MapProvider.displayName = MapProvider.name;
var useMap = () => {
	const context = useContext(MapContext);
	if (!context) throw new Error("No context found for Map");
	return context;
};
//#endregion
//#region src/components/Map/Map.types.ts
/** Visual theme for map overlay controls (zoom, fullscreen). */
var MapTheme = {
	LIGHT: "light",
	DARK: "dark"
};
var RouteTransportTypes = {
	AIR: "air",
	SEA: "sea",
	ROAD: "road",
	RAIL: "rail",
	OTHER: "other"
};
//#endregion
//#region src/lib/logMaplibError.lib.ts
var LOG_PREFIX = "[@qubictron/maplib]";
function logMaplibError(message, detail) {
	if (detail === void 0) {
		console.error(`${LOG_PREFIX} ${message}`);
		return;
	}
	console.error(`${LOG_PREFIX} ${message}`, detail);
}
//#endregion
//#region src/components/Map/Map.utils.ts
function toCoordinate(lngLat) {
	if (Array.isArray(lngLat)) {
		const lng = lngLat[0];
		const lat = lngLat[1];
		if (typeof lng === "number" && typeof lat === "number") return {
			lng,
			lat
		};
		logMaplibError("Invalid LngLatLike coordinate: expected [lng, lat] tuple", lngLat);
		return null;
	}
	if (typeof lngLat === "object" && lngLat !== null) {
		if ("lng" in lngLat && typeof lngLat.lng === "number" && typeof lngLat.lat === "number") return {
			lng: lngLat.lng,
			lat: lngLat.lat
		};
		if ("lon" in lngLat && typeof lngLat.lon === "number" && typeof lngLat.lat === "number") return {
			lng: lngLat.lon,
			lat: lngLat.lat
		};
	}
	logMaplibError("Invalid LngLatLike coordinate", lngLat);
	return null;
}
/** Unwraps longitude relative to the previous point so routes crossing the antimeridian fit correctly. */
function createAntimeridianFixedPoints(points) {
	const [first, ...rest] = points;
	if (first === void 0) return [];
	const out = [first];
	for (const point of rest) {
		const prev = out.at(-1);
		if (prev === void 0) break;
		let lng = point.lng;
		const delta = lng - prev.lng;
		if (delta > 180) lng -= 360;
		else if (delta < -180) lng += 360;
		out.push({
			lat: point.lat,
			lng
		});
	}
	return out;
}
function boundsFromCoordinates(coordinates) {
	const [first, ...rest] = coordinates;
	if (first === void 0) return null;
	let minLng = first.lng;
	let maxLng = first.lng;
	let minLat = first.lat;
	let maxLat = first.lat;
	for (const { lng, lat } of rest) {
		if (lng < minLng) minLng = lng;
		if (lng > maxLng) maxLng = lng;
		if (lat < minLat) minLat = lat;
		if (lat > maxLat) maxLat = lat;
	}
	return [[minLng, minLat], [maxLng, maxLat]];
}
function fitMapToCoordinates(map, coordinates, options) {
	if (coordinates.length === 0) return;
	const normalized = coordinates.flatMap((coordinate) => {
		const parsed = toCoordinate(coordinate);
		return parsed ? [parsed] : [];
	});
	if (normalized.length === 0) {
		logMaplibError("fitMapToCoordinates: no valid coordinates to fit");
		return;
	}
	const bounds = boundsFromCoordinates(createAntimeridianFixedPoints(normalized));
	if (!bounds) return;
	const fitOptions = {
		animate: true,
		essential: true,
		...options
	};
	if (map.getProjection?.()?.type === "globe") {
		const camera = map.cameraForBounds(bounds, fitOptions);
		if (camera) {
			const fittedZoom = camera.zoom ?? 2.2;
			const zoom = Math.min(Math.max(fittedZoom - GLOBE_ROUTE_ZOOM_OUT, DEFAULT_GLOBE_ZOOM), GLOBE_ROUTE_MAX_ZOOM);
			map.flyTo({
				...camera,
				zoom,
				essential: true
			});
			return;
		}
		const [[minLng, minLat], [maxLng, maxLat]] = bounds;
		map.flyTo({
			center: [(minLng + maxLng) / 2, (minLat + maxLat) / 2],
			zoom: DEFAULT_GLOBE_ZOOM,
			essential: true
		});
		return;
	}
	map.fitBounds(bounds, fitOptions);
}
var DEFAULT_ORIGIN_CENTER = [0, 0];
var DEFAULT_ORIGIN_ZOOM = 2;
var DEFAULT_FIT_BOUNDS_PADDING = 60;
var DEFAULT_MARKER_SIZE = 24;
var DEFAULT_POINT_CIRCLE_RADIUS = 6;
var DEFAULT_POINT_CIRCLE_COLOR = "#007cbf";
var DEFAULT_POINT_CIRCLE_SCALE = 1.2;
var DEFAULT_CLUSTER_MAX_ZOOM = 18;
var DEFAULT_CLUSTER_RADIUS = 20;
var DEFAULT_CLUSTER_CIRCLE_COLOR = "#08F";
var DEFAULT_CLUSTER_TEXT_COLOR = "#ffffff";
var DEFAULT_CONTROL_RIGHT = 16;
var DEFAULT_ZOOM_CONTROL_BOTTOM = 24;
/** Zoom stack height (px): two 28px buttons + 4px gap. */
var ZOOM_CONTROL_HEIGHT = 60;
var CONTROL_GAP = 8;
var DEFAULT_FULLSCREEN_CONTROL_BOTTOM = DEFAULT_ZOOM_CONTROL_BOTTOM + ZOOM_CONTROL_HEIGHT + CONTROL_GAP;
var DEFAULT_GLOBE_CONTROL_BOTTOM = DEFAULT_FULLSCREEN_CONTROL_BOTTOM + 28 + CONTROL_GAP;
/** Minimum zoom applied when switching to the globe, so the sphere reads as a globe, not a dot. */
var DEFAULT_GLOBE_ZOOM = 2.2;
/** Pull the globe camera back this many zoom levels vs a tight 2D fit. */
var GLOBE_ROUTE_ZOOM_OUT = 1;
/** Keep globe framing wide enough that the sphere still reads as a globe. */
var GLOBE_ROUTE_MAX_ZOOM = 3.2;
var DEFAULT_LINE_WIDTH = 3;
var DEFAULT_LINE_OPACITY = 1;
var DEFAULT_LINE_DASHARRAY = [1, 2.5];
var DEFAULT_THEME = MapTheme.DARK;
var resolveMapControlOptions = (options, defaults) => ({
	visible: options?.visible ?? true,
	right: options?.right ?? DEFAULT_CONTROL_RIGHT,
	bottom: options?.bottom ?? defaults?.bottom ?? DEFAULT_ZOOM_CONTROL_BOTTOM,
	left: options?.left,
	top: options?.top
});
var resolveZoomControlOptions = (options) => resolveMapControlOptions(options, { bottom: DEFAULT_ZOOM_CONTROL_BOTTOM });
var resolveFullscreenControlOptions = (options) => {
	if (!options) return void 0;
	const { icon, exitIcon, ...position } = options;
	return {
		icon,
		exitIcon,
		right: position.right ?? DEFAULT_CONTROL_RIGHT,
		bottom: position.bottom ?? DEFAULT_FULLSCREEN_CONTROL_BOTTOM,
		left: position.left,
		top: position.top
	};
};
var resolveGlobeControlOptions = (options) => {
	if (!options) return void 0;
	const { icon, globeIcon, zoom, ...position } = options;
	return {
		icon,
		globeIcon,
		zoom: zoom ?? 2.2,
		right: position.right ?? DEFAULT_CONTROL_RIGHT,
		bottom: position.bottom ?? DEFAULT_GLOBE_CONTROL_BOTTOM,
		left: position.left,
		top: position.top
	};
};
var resolveFitBoundsPadding = (padding) => padding ?? DEFAULT_FIT_BOUNDS_PADDING;
/** Normalizes `fitBoundsPadding` into a fully-populated `PaddingOptions` for `map.setPadding`. */
var toPaddingOptions = (padding) => {
	if (typeof padding === "number") return {
		top: padding,
		right: padding,
		bottom: padding,
		left: padding
	};
	return {
		top: padding.top ?? 0,
		right: padding.right ?? 0,
		bottom: padding.bottom ?? 0,
		left: padding.left ?? 0
	};
};
var resolvePointDefaults = (options) => ({
	markerSize: options?.markerSize ?? DEFAULT_MARKER_SIZE,
	circleRadius: options?.circleRadius ?? DEFAULT_POINT_CIRCLE_RADIUS,
	circleColor: options?.circleColor ?? DEFAULT_POINT_CIRCLE_COLOR,
	circleScale: options?.circleScale ?? DEFAULT_POINT_CIRCLE_SCALE,
	clusterMaxZoom: options?.clusterMaxZoom ?? DEFAULT_CLUSTER_MAX_ZOOM,
	clusterRadius: options?.clusterRadius ?? DEFAULT_CLUSTER_RADIUS,
	clusterCircleColor: options?.clusterCircleColor ?? DEFAULT_CLUSTER_CIRCLE_COLOR,
	clusterTextColor: options?.clusterTextColor ?? DEFAULT_CLUSTER_TEXT_COLOR
});
var resolveRouteDefaults = (options) => ({
	lineWidth: options?.lineWidth ?? DEFAULT_LINE_WIDTH,
	lineOpacity: options?.lineOpacity ?? DEFAULT_LINE_OPACITY,
	lineDashArray: options?.lineDashArray ?? DEFAULT_LINE_DASHARRAY
});
/** Applies library defaults to `InitOptions` (everything except `container`). */
var resolveInitOptions = (options) => ({
	style: options.style,
	center: options.center ?? DEFAULT_ORIGIN_CENTER,
	zoom: options.zoom ?? DEFAULT_ORIGIN_ZOOM,
	fitBoundsPadding: resolveFitBoundsPadding(options.fitBoundsPadding),
	pointDefaults: resolvePointDefaults(options.pointDefaults),
	routeDefaults: resolveRouteDefaults(options.routeDefaults),
	zoomControl: resolveZoomControlOptions(options.zoomControl),
	fullscreenControl: resolveFullscreenControlOptions(options.fullscreenControl),
	globeControl: resolveGlobeControlOptions(options.globeControl),
	theme: options.theme ?? DEFAULT_THEME
});
var centerDep = (center) => {
	if (center == null) return void 0;
	if (Array.isArray(center)) return `${center[0]},${center[1]}`;
	if (typeof center === "object" && "lng" in center && "lat" in center) return `${center.lng},${center.lat}`;
	return String(center);
};
/** Primitive deps for syncing `initOptions` → `MapEngine` without object-identity noise. */
var getInitOptionsSyncDeps = (options) => {
	const padding = options.fitBoundsPadding;
	const point = options.pointDefaults;
	const route = options.routeDefaults;
	const zoom = options.zoomControl;
	const fullscreen = options.fullscreenControl;
	const globe = options.globeControl;
	return [
		options.style,
		centerDep(options.center),
		options.zoom,
		typeof padding === "number" ? padding : padding?.top,
		typeof padding === "number" ? padding : padding?.right,
		typeof padding === "number" ? padding : padding?.bottom,
		typeof padding === "number" ? padding : padding?.left,
		point?.markerSize,
		point?.circleRadius,
		point?.circleColor,
		point?.circleScale,
		point?.clusterMaxZoom,
		point?.clusterRadius,
		point?.clusterCircleColor,
		point?.clusterTextColor,
		route?.lineWidth,
		route?.lineOpacity,
		route?.lineDashArray?.[0],
		route?.lineDashArray?.[1],
		zoom?.visible,
		zoom?.right,
		zoom?.bottom,
		zoom?.left,
		zoom?.top,
		fullscreen?.right,
		fullscreen?.bottom,
		fullscreen?.left,
		fullscreen?.top,
		globe?.right,
		globe?.bottom,
		globe?.left,
		globe?.top,
		globe?.zoom,
		options.theme
	];
};
/**
* Merges the caller-supplied options with the library defaults.
* Every default value in MapEngine must be defined here — nowhere else.
*/
var defineDefaultOptions = (options) => {
	const { zoomControl: _zoomControl, fullscreenControl: _fullscreenControl, globeControl: _globeControl, ...rest } = options;
	const resolved = resolveInitOptions(options);
	return {
		...rest,
		...resolved
	};
};
//#endregion
//#region src/components/Map/controls/controlPosition.ts
var getControlPositionStyle = (options) => {
	const style = {};
	if (options.left != null) style.left = options.left;
	else style.right = options.right;
	if (options.top != null) style.top = options.top;
	else style.bottom = options.bottom;
	return style;
};
var Control_module_default = {
	controlStack: "controlStack--controls--kgY",
	controlSingle: "controlSingle--controls--9xL",
	button: "button--controls--zky"
};
//#endregion
//#region src/components/Map/controls/FullscreenControl/FullscreenControl.tsx
var _jsxFileName$7 = "/home/forge/apps.dev.qubictron.com/packages/maplib/src/components/Map/controls/FullscreenControl/FullscreenControl.tsx";
var DefaultEnterIcon = () => /* @__PURE__ */ jsxDEV("svg", {
	xmlns: "http://www.w3.org/2000/svg",
	width: "12",
	height: "12",
	viewBox: "0 0 12 12",
	fill: "none",
	"aria-hidden": true,
	children: /* @__PURE__ */ jsxDEV("path", {
		stroke: "currentColor",
		strokeWidth: "1.2",
		strokeLinecap: "round",
		strokeLinejoin: "round",
		d: "M1.5 4.5V1.5h3M7.5 1.5h3v3M10.5 7.5v3H7.5M1.5 7.5v3h3"
	}, void 0, false, {
		fileName: _jsxFileName$7,
		lineNumber: 8,
		columnNumber: 5
	}, void 0)
}, void 0, false, {
	fileName: _jsxFileName$7,
	lineNumber: 7,
	columnNumber: 3
}, void 0);
var DefaultExitIcon = () => /* @__PURE__ */ jsxDEV("svg", {
	xmlns: "http://www.w3.org/2000/svg",
	width: "12",
	height: "12",
	viewBox: "0 0 12 12",
	fill: "none",
	"aria-hidden": true,
	children: /* @__PURE__ */ jsxDEV("path", {
		stroke: "currentColor",
		strokeWidth: "1.2",
		strokeLinecap: "round",
		strokeLinejoin: "round",
		d: "M4.5 1.5H1.5v3M7.5 1.5h3v3M10.5 7.5v3H7.5M4.5 10.5H1.5V7.5"
	}, void 0, false, {
		fileName: _jsxFileName$7,
		lineNumber: 20,
		columnNumber: 5
	}, void 0)
}, void 0, false, {
	fileName: _jsxFileName$7,
	lineNumber: 19,
	columnNumber: 3
}, void 0);
var FullscreenControl = () => {
	const { engine, initOptions, mapContainerRef } = useMap();
	const fullscreenControl = initOptions.fullscreenControl;
	const [isFullscreen, setIsFullscreen] = useState(false);
	useEffect(() => {
		const container = mapContainerRef.current;
		if (!container) return;
		const syncFullscreenState = () => {
			setIsFullscreen(document.fullscreenElement === container);
			engine.map.resize();
		};
		document.addEventListener("fullscreenchange", syncFullscreenState);
		return () => {
			document.removeEventListener("fullscreenchange", syncFullscreenState);
		};
	}, [engine, mapContainerRef]);
	if (!fullscreenControl) return null;
	const { icon, exitIcon, right, bottom, left, top } = fullscreenControl;
	const toggleFullscreen = async () => {
		const container = mapContainerRef.current;
		if (!container) return;
		try {
			if (document.fullscreenElement === container) await document.exitFullscreen();
			else await container.requestFullscreen();
		} catch {}
	};
	const enterIcon = icon ?? /* @__PURE__ */ jsxDEV(DefaultEnterIcon, {}, void 0, false, {
		fileName: _jsxFileName$7,
		lineNumber: 73,
		columnNumber: 29
	}, void 0);
	const leaveIcon = exitIcon ?? /* @__PURE__ */ jsxDEV(DefaultExitIcon, {}, void 0, false, {
		fileName: _jsxFileName$7,
		lineNumber: 74,
		columnNumber: 33
	}, void 0);
	return /* @__PURE__ */ jsxDEV("div", {
		className: Control_module_default.controlSingle,
		style: getControlPositionStyle({
			right,
			bottom,
			left,
			top
		}),
		children: /* @__PURE__ */ jsxDEV("button", {
			className: Control_module_default.button,
			onClick: toggleFullscreen,
			"aria-label": isFullscreen ? "Exit fullscreen" : "Enter fullscreen",
			type: "button",
			children: isFullscreen ? leaveIcon : enterIcon
		}, void 0, false, {
			fileName: _jsxFileName$7,
			lineNumber: 78,
			columnNumber: 7
		}, void 0)
	}, void 0, false, {
		fileName: _jsxFileName$7,
		lineNumber: 77,
		columnNumber: 5
	}, void 0);
};
//#endregion
//#region src/components/Map/controls/GlobeControl/GlobeControl.tsx
var _jsxFileName$6 = "/home/forge/apps.dev.qubictron.com/packages/maplib/src/components/Map/controls/GlobeControl/GlobeControl.tsx";
var GlobeIcon = () => /* @__PURE__ */ jsxDEV("svg", {
	xmlns: "http://www.w3.org/2000/svg",
	width: "14",
	height: "14",
	viewBox: "0 0 14 14",
	fill: "none",
	"aria-hidden": true,
	children: [/* @__PURE__ */ jsxDEV("circle", {
		cx: "7",
		cy: "7",
		r: "5.5",
		stroke: "currentColor",
		strokeWidth: "1.2"
	}, void 0, false, {
		fileName: _jsxFileName$6,
		lineNumber: 8,
		columnNumber: 5
	}, void 0), /* @__PURE__ */ jsxDEV("path", {
		stroke: "currentColor",
		strokeWidth: "1.2",
		d: "M1.5 7h11M7 1.5c1.53 1.47 2.4 3.43 2.4 5.5S8.53 12.03 7 13.5C5.47 12.03 4.6 10.07 4.6 8S5.47 1.97 7 1.5Z"
	}, void 0, false, {
		fileName: _jsxFileName$6,
		lineNumber: 9,
		columnNumber: 5
	}, void 0)]
}, void 0, true, {
	fileName: _jsxFileName$6,
	lineNumber: 7,
	columnNumber: 3
}, void 0);
var FlatMapIcon = () => /* @__PURE__ */ jsxDEV("svg", {
	xmlns: "http://www.w3.org/2000/svg",
	width: "14",
	height: "14",
	viewBox: "0 0 14 14",
	fill: "none",
	"aria-hidden": true,
	children: [/* @__PURE__ */ jsxDEV("path", {
		stroke: "currentColor",
		strokeWidth: "1.2",
		strokeLinejoin: "round",
		d: "M1.5 3.2 5 1.5l4 2 3.5-1.7v9.1L9 12.5l-4-2-3.5 1.7z"
	}, void 0, false, {
		fileName: _jsxFileName$6,
		lineNumber: 19,
		columnNumber: 5
	}, void 0), /* @__PURE__ */ jsxDEV("path", {
		stroke: "currentColor",
		strokeWidth: "1.2",
		d: "M5 1.5v9M9 3.5v9"
	}, void 0, false, {
		fileName: _jsxFileName$6,
		lineNumber: 25,
		columnNumber: 5
	}, void 0)]
}, void 0, true, {
	fileName: _jsxFileName$6,
	lineNumber: 18,
	columnNumber: 3
}, void 0);
var GlobeControl = () => {
	const { engine, initOptions } = useMap();
	const globeControl = initOptions.globeControl;
	const [isGlobe, setIsGlobe] = useState(() => engine.map.getProjection?.()?.type === "globe");
	const isGlobeRef = useRef(isGlobe);
	const preGlobeZoomRef = useRef(null);
	isGlobeRef.current = isGlobe;
	useEffect(() => {
		const map = engine.map;
		const syncProjectionState = () => {
			if (isGlobeRef.current) engine.setMapProjection("globe");
			else engine.syncProjectionAttr();
			setIsGlobe(map.getProjection?.()?.type === "globe");
		};
		map.on("style.load", syncProjectionState);
		return () => {
			map.off("style.load", syncProjectionState);
		};
	}, [engine]);
	if (!globeControl) return null;
	const { icon, globeIcon, zoom, right, bottom, left, top } = globeControl;
	const toggleProjection = () => {
		const nextIsGlobe = !isGlobe;
		engine.setMapProjection(nextIsGlobe ? "globe" : "mercator");
		if (engine.hasRenderedRoute()) {
			engine.flyToRoute();
			preGlobeZoomRef.current = null;
		} else if (nextIsGlobe) {
			preGlobeZoomRef.current = engine.map.getZoom();
			if (preGlobeZoomRef.current < zoom) engine.map.easeTo({
				zoom,
				essential: true
			});
		} else if (preGlobeZoomRef.current != null) {
			engine.map.easeTo({
				zoom: preGlobeZoomRef.current,
				essential: true
			});
			preGlobeZoomRef.current = null;
		}
		setIsGlobe(nextIsGlobe);
	};
	const flatIcon = icon ?? /* @__PURE__ */ jsxDEV(FlatMapIcon, {}, void 0, false, {
		fileName: _jsxFileName$6,
		lineNumber: 86,
		columnNumber: 28
	}, void 0);
	const sphereIcon = globeIcon ?? /* @__PURE__ */ jsxDEV(GlobeIcon, {}, void 0, false, {
		fileName: _jsxFileName$6,
		lineNumber: 87,
		columnNumber: 35
	}, void 0);
	return /* @__PURE__ */ jsxDEV("div", {
		className: Control_module_default.controlSingle,
		style: getControlPositionStyle({
			right,
			bottom,
			left,
			top
		}),
		children: /* @__PURE__ */ jsxDEV("button", {
			className: Control_module_default.button,
			onClick: toggleProjection,
			"aria-label": isGlobe ? "Switch to flat map" : "Switch to globe view",
			"aria-pressed": isGlobe,
			type: "button",
			children: isGlobe ? flatIcon : sphereIcon
		}, void 0, false, {
			fileName: _jsxFileName$6,
			lineNumber: 91,
			columnNumber: 7
		}, void 0)
	}, void 0, false, {
		fileName: _jsxFileName$6,
		lineNumber: 90,
		columnNumber: 5
	}, void 0);
};
/*!
Copyright (c) 2018 Jed Watson.
Licensed under the MIT License (MIT), see
http://jedwatson.github.io/classnames
*/
//#endregion
//#region src/components/Map/controls/ZoomControl/ZoomControl.tsx
var import_classnames = /* @__PURE__ */ __toESM((/* @__PURE__ */ __commonJSMin(((exports, module) => {
	(function() {
		"use strict";
		var hasOwn = {}.hasOwnProperty;
		function classNames() {
			var classes = "";
			for (var i = 0; i < arguments.length; i++) {
				var arg = arguments[i];
				if (arg) classes = appendClass(classes, parseValue(arg));
			}
			return classes;
		}
		function parseValue(arg) {
			if (typeof arg === "string" || typeof arg === "number") return arg;
			if (typeof arg !== "object") return "";
			if (Array.isArray(arg)) return classNames.apply(null, arg);
			if (arg.toString !== Object.prototype.toString && !arg.toString.toString().includes("[native code]")) return arg.toString();
			var classes = "";
			for (var key in arg) if (hasOwn.call(arg, key) && arg[key]) classes = appendClass(classes, key);
			return classes;
		}
		function appendClass(value, newClass) {
			if (!newClass) return value;
			if (value) return value + " " + newClass;
			return value + newClass;
		}
		if (typeof module !== "undefined" && module.exports) {
			classNames.default = classNames;
			module.exports = classNames;
		} else if (typeof define === "function" && typeof define.amd === "object" && define.amd) define("classnames", [], function() {
			return classNames;
		});
		else window.classNames = classNames;
	})();
})))());
var _jsxFileName$5 = "/home/forge/apps.dev.qubictron.com/packages/maplib/src/components/Map/controls/ZoomControl/ZoomControl.tsx";
var PlusIcon = () => /* @__PURE__ */ jsxDEV("svg", {
	xmlns: "http://www.w3.org/2000/svg",
	width: "12",
	height: "12",
	fill: "none",
	viewBox: "0 0 12 12",
	"aria-hidden": true,
	children: /* @__PURE__ */ jsxDEV("path", {
		fill: "currentColor",
		d: "M5.22 6.78H3.22a.78.78 0 1 1 0-1.56h2V3.22a.78.78 0 1 1 1.56 0v2h2a.78.78 0 1 1 0 1.56H6.78v2a.78.78 0 1 1-1.56 0z"
	}, void 0, false, {
		fileName: _jsxFileName$5,
		lineNumber: 8,
		columnNumber: 5
	}, void 0)
}, void 0, false, {
	fileName: _jsxFileName$5,
	lineNumber: 7,
	columnNumber: 3
}, void 0);
var MinusIcon = () => /* @__PURE__ */ jsxDEV("svg", {
	xmlns: "http://www.w3.org/2000/svg",
	width: "12",
	height: "12",
	fill: "none",
	viewBox: "0 0 12 12",
	"aria-hidden": true,
	children: /* @__PURE__ */ jsxDEV("path", {
		fill: "currentColor",
		d: "M3.29 5.5h5.42c.43 0 .79.35.79.79s-.36.79-.79.79H3.29a.79.79 0 1 1 0-1.58"
	}, void 0, false, {
		fileName: _jsxFileName$5,
		lineNumber: 17,
		columnNumber: 5
	}, void 0)
}, void 0, false, {
	fileName: _jsxFileName$5,
	lineNumber: 16,
	columnNumber: 3
}, void 0);
var ZoomControl = () => {
	const { engine, initOptions } = useMap();
	const { visible, right, bottom, left, top } = initOptions.zoomControl;
	if (!visible) return null;
	const handleZoomIn = () => engine.map.zoomIn();
	const handleZoomOut = () => engine.map.zoomOut();
	return /* @__PURE__ */ jsxDEV("div", {
		className: (0, import_classnames.default)(Control_module_default.controlStack),
		style: getControlPositionStyle({
			right,
			bottom,
			left,
			top
		}),
		children: [/* @__PURE__ */ jsxDEV("button", {
			className: Control_module_default.button,
			onClick: handleZoomIn,
			"aria-label": "Zoom in",
			type: "button",
			children: /* @__PURE__ */ jsxDEV(PlusIcon, {}, void 0, false, {
				fileName: _jsxFileName$5,
				lineNumber: 35,
				columnNumber: 9
			}, void 0)
		}, void 0, false, {
			fileName: _jsxFileName$5,
			lineNumber: 34,
			columnNumber: 7
		}, void 0), /* @__PURE__ */ jsxDEV("button", {
			className: Control_module_default.button,
			onClick: handleZoomOut,
			"aria-label": "Zoom out",
			type: "button",
			children: /* @__PURE__ */ jsxDEV(MinusIcon, {}, void 0, false, {
				fileName: _jsxFileName$5,
				lineNumber: 38,
				columnNumber: 9
			}, void 0)
		}, void 0, false, {
			fileName: _jsxFileName$5,
			lineNumber: 37,
			columnNumber: 7
		}, void 0)]
	}, void 0, true, {
		fileName: _jsxFileName$5,
		lineNumber: 33,
		columnNumber: 5
	}, void 0);
};
//#endregion
//#region src/components/Map/MapCore.tsx
var _jsxFileName$4 = "/home/forge/apps.dev.qubictron.com/packages/maplib/src/components/Map/MapCore.tsx";
var MapCore = () => {
	useMap();
	return /* @__PURE__ */ jsxDEV(Fragment, { children: [
		/* @__PURE__ */ jsxDEV(FullscreenControl, {}, void 0, false, {
			fileName: _jsxFileName$4,
			lineNumber: 11,
			columnNumber: 7
		}, void 0),
		/* @__PURE__ */ jsxDEV(GlobeControl, {}, void 0, false, {
			fileName: _jsxFileName$4,
			lineNumber: 12,
			columnNumber: 7
		}, void 0),
		/* @__PURE__ */ jsxDEV(ZoomControl, {}, void 0, false, {
			fileName: _jsxFileName$4,
			lineNumber: 13,
			columnNumber: 7
		}, void 0)
	] }, void 0, true, {
		fileName: _jsxFileName$4,
		lineNumber: 10,
		columnNumber: 5
	}, void 0);
};
//#endregion
//#region src/components/Presets/Lines/Lines.ts
var seaColorLine = "#60A5FA";
var airColorLine = "#F52C5C";
var roadColorLine = "#1B7F38";
var railColorLine = "#C36319";
var otherColorLine = "#E9B36B";
var Icons_module_default = {
	icon16x16: "icon16x16--Icons--OrM",
	icon20x20: "icon20x20--Icons--HZ1",
	icon40x40: "icon40x40--Icons--nRo"
};
//#endregion
//#region src/components/Presets/Icons/Icons.tsx
var _jsxFileName$3 = "/home/forge/apps.dev.qubictron.com/packages/maplib/src/components/Presets/Icons/Icons.tsx";
var withDefaultStyles = (Component, variant) => ({ className, ...rest }) => /* @__PURE__ */ jsxDEV(Component, {
	className: (0, import_classnames.default)({ [Icons_module_default[`icon${variant}`]]: !!variant }, className),
	...rest
}, void 0, false, {
	fileName: _jsxFileName$3,
	lineNumber: 12,
	columnNumber: 5
}, void 0);
var RedCircleIcon = withDefaultStyles((props) => /* @__PURE__ */ jsxDEV("svg", {
	xmlns: "http://www.w3.org/2000/svg",
	width: "10",
	height: "10",
	fill: "none",
	viewBox: "0 0 10 10",
	...props,
	children: /* @__PURE__ */ jsxDEV("circle", {
		cx: "5",
		cy: "5",
		r: "4.334",
		fill: "#F52C5C",
		stroke: "#fff"
	}, void 0, false, {
		fileName: _jsxFileName$3,
		lineNumber: 17,
		columnNumber: 5
	}, void 0)
}, void 0, false, {
	fileName: _jsxFileName$3,
	lineNumber: 16,
	columnNumber: 3
}, void 0));
var GreenCircleIcon = withDefaultStyles((props) => /* @__PURE__ */ jsxDEV("svg", {
	xmlns: "http://www.w3.org/2000/svg",
	width: "11",
	height: "11",
	fill: "none",
	viewBox: "0 0 11 11",
	...props,
	children: /* @__PURE__ */ jsxDEV("path", {
		fill: "#1B7F38",
		d: "M5.395 10.79a5.395 5.395 0 1 0 0-10.79 5.395 5.395 0 0 0 0 10.79",
		opacity: "0.8"
	}, void 0, false, {
		fileName: _jsxFileName$3,
		lineNumber: 23,
		columnNumber: 5
	}, void 0)
}, void 0, false, {
	fileName: _jsxFileName$3,
	lineNumber: 22,
	columnNumber: 3
}, void 0));
var BlueCircleIcon = withDefaultStyles((props) => /* @__PURE__ */ jsxDEV("svg", {
	xmlns: "http://www.w3.org/2000/svg",
	width: "11",
	height: "11",
	fill: "none",
	viewBox: "0 0 11 11",
	...props,
	children: /* @__PURE__ */ jsxDEV("path", {
		fill: "#60A5FA",
		d: "M5.395 10.79a5.395 5.395 0 1 0 0-10.79 5.395 5.395 0 0 0 0 10.79"
	}, void 0, false, {
		fileName: _jsxFileName$3,
		lineNumber: 29,
		columnNumber: 5
	}, void 0)
}, void 0, false, {
	fileName: _jsxFileName$3,
	lineNumber: 28,
	columnNumber: 3
}, void 0));
var OrangeCircleIcon = withDefaultStyles((props) => /* @__PURE__ */ jsxDEV("svg", {
	xmlns: "http://www.w3.org/2000/svg",
	width: "11",
	height: "11",
	fill: "none",
	viewBox: "0 0 11 11",
	...props,
	children: /* @__PURE__ */ jsxDEV("path", {
		fill: "#C36319",
		d: "M5.395 10.79a5.395 5.395 0 1 0 0-10.79 5.395 5.395 0 0 0 0 10.79",
		opacity: "0.8"
	}, void 0, false, {
		fileName: _jsxFileName$3,
		lineNumber: 35,
		columnNumber: 5
	}, void 0)
}, void 0, false, {
	fileName: _jsxFileName$3,
	lineNumber: 34,
	columnNumber: 3
}, void 0));
var OtherCircleIcon = withDefaultStyles((props) => /* @__PURE__ */ jsxDEV("svg", {
	xmlns: "http://www.w3.org/2000/svg",
	width: "11",
	height: "11",
	fill: "none",
	viewBox: "0 0 11 11",
	...props,
	children: /* @__PURE__ */ jsxDEV("circle", {
		cx: "5.324",
		cy: "5.324",
		r: "4.659",
		fill: "#E9B36B",
		stroke: "#fff",
		strokeWidth: "1.331"
	}, void 0, false, {
		fileName: _jsxFileName$3,
		lineNumber: 41,
		columnNumber: 5
	}, void 0)
}, void 0, false, {
	fileName: _jsxFileName$3,
	lineNumber: 40,
	columnNumber: 3
}, void 0));
var ChevronRight = withDefaultStyles((props) => /* @__PURE__ */ jsxDEV("svg", {
	xmlns: "http://www.w3.org/2000/svg",
	width: "10",
	height: "10",
	fill: "none",
	viewBox: "0 0 10 10",
	...props,
	children: /* @__PURE__ */ jsxDEV("path", {
		d: "M3.5 2L6.5 5L3.5 8",
		stroke: "#000C37",
		strokeWidth: "1.5",
		strokeLinecap: "round",
		strokeLinejoin: "round"
	}, void 0, false, {
		fileName: _jsxFileName$3,
		lineNumber: 47,
		columnNumber: 5
	}, void 0)
}, void 0, false, {
	fileName: _jsxFileName$3,
	lineNumber: 46,
	columnNumber: 3
}, void 0));
var Points_module_default = { point: "point--Points--Z-x" };
//#endregion
//#region src/components/Presets/Points/Points.tsx
var _jsxFileName$2 = "/home/forge/apps.dev.qubictron.com/packages/maplib/src/components/Presets/Points/Points.tsx";
var AirPoint = () => {
	return /* @__PURE__ */ jsxDEV(RedCircleIcon, {
		className: (0, import_classnames.default)(Points_module_default.airPoint, Points_module_default.point),
		width: 12,
		height: 12
	}, void 0, false, {
		fileName: _jsxFileName$2,
		lineNumber: 6,
		columnNumber: 10
	}, void 0);
};
var SeaPoint = () => {
	return /* @__PURE__ */ jsxDEV(BlueCircleIcon, {
		className: (0, import_classnames.default)(Points_module_default.seaPoint, Points_module_default.point),
		width: 12,
		height: 12
	}, void 0, false, {
		fileName: _jsxFileName$2,
		lineNumber: 10,
		columnNumber: 10
	}, void 0);
};
var RoadPoint = () => {
	return /* @__PURE__ */ jsxDEV(GreenCircleIcon, {
		className: (0, import_classnames.default)(Points_module_default.roadPoint, Points_module_default.point),
		width: 12,
		height: 12
	}, void 0, false, {
		fileName: _jsxFileName$2,
		lineNumber: 14,
		columnNumber: 10
	}, void 0);
};
var RailPoint = () => {
	return /* @__PURE__ */ jsxDEV(OrangeCircleIcon, {
		className: (0, import_classnames.default)(Points_module_default.railPoint, Points_module_default.point),
		width: 12,
		height: 12
	}, void 0, false, {
		fileName: _jsxFileName$2,
		lineNumber: 18,
		columnNumber: 10
	}, void 0);
};
var OtherPoint = () => {
	return /* @__PURE__ */ jsxDEV(OtherCircleIcon, {
		className: (0, import_classnames.default)(Points_module_default.otherPoint, Points_module_default.point),
		width: 12,
		height: 12
	}, void 0, false, {
		fileName: _jsxFileName$2,
		lineNumber: 22,
		columnNumber: 10
	}, void 0);
};
//#endregion
//#region src/components/Presets/constants.ts
var pointPresetMap = {
	[RouteTransportTypes.AIR]: AirPoint,
	[RouteTransportTypes.SEA]: SeaPoint,
	[RouteTransportTypes.ROAD]: RoadPoint,
	[RouteTransportTypes.RAIL]: RailPoint,
	[RouteTransportTypes.OTHER]: OtherPoint
};
var linePresetMap = {
	[RouteTransportTypes.AIR]: airColorLine,
	[RouteTransportTypes.SEA]: seaColorLine,
	[RouteTransportTypes.ROAD]: roadColorLine,
	[RouteTransportTypes.RAIL]: railColorLine,
	[RouteTransportTypes.OTHER]: otherColorLine
};
//#endregion
//#region src/components/Map/entities/Point/point.utils.ts
/**
* Fill colors for the GeoJSON circle layer when points are rendered in world-copy mode.
* These match the fill colors used in the SVG preset icon components (Icons.tsx).
*/
var transportPointCircleColor = {
	[RouteTransportTypes.SEA]: "#08F",
	[RouteTransportTypes.AIR]: "#F52C5C",
	[RouteTransportTypes.ROAD]: "#02BCB1",
	[RouteTransportTypes.RAIL]: "#FC7E1D",
	[RouteTransportTypes.OTHER]: "#E9B36B"
};
var EARTH_RADIUS_METERS = 6371e3;
function lngLatTuple(coordinate) {
	return [coordinate.lng, coordinate.lat];
}
function pointStableKey(point, index) {
	if (point.id != null) return typeof point.id === "number" ? `n:${point.id}` : `s:${point.id}`;
	return `i:${index}`;
}
function hasIconMarker(marker) {
	if (marker == null || typeof marker !== "object") return false;
	if (!("src" in marker)) return false;
	return typeof marker.src === "string" && marker.src.length > 0;
}
/**
* Returns `true` when the marker is a `PointCircleMarker` — a plain object
* requesting a native GeoJSON circle on the map (not a DOM element or React node).
*
* Detection logic: plain object, not a React element (`$$typeof`), not an icon (`src`).
*/
function hasCircleMarker(marker) {
	if (marker == null || typeof marker !== "object") return false;
	const o = marker;
	return !("$$typeof" in o) && !("src" in o);
}
function isRenderableReactMarker(marker) {
	if (hasIconMarker(marker)) return false;
	if (marker == null || marker === false) return false;
	if (typeof marker === "string" || typeof marker === "number") return true;
	if (isValidElement(marker)) return true;
	if (Array.isArray(marker)) return marker.some((n) => n != null && n !== false);
	return false;
}
/** Explicit `marker`, or preset from `transportType` (`RouteTransportTypes`); if omitted → sea. */
function resolveEffectiveMarker(point) {
	if (hasIconMarker(point.marker)) return point.marker;
	if (isRenderableReactMarker(point.marker)) return point.marker;
	const Preset = pointPresetMap[point.transportType ?? RouteTransportTypes.SEA];
	return createElement(Preset);
}
function isRenderablePopup(popup) {
	if (popup == null || popup === false) return false;
	if (typeof popup === "string" || typeof popup === "number") return true;
	if (isValidElement(popup)) return true;
	if (Array.isArray(popup)) return popup.some((n) => n != null && n !== false);
	return false;
}
/** Great-circle distance between two coordinates in meters. */
function distanceMeters(a, b) {
	const toRad = (deg) => deg * Math.PI / 180;
	const dLat = toRad(b.lat - a.lat);
	const dLng = toRad(b.lng - a.lng);
	const lat1 = toRad(a.lat);
	const lat2 = toRad(b.lat);
	const sinHalf = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
	return 2 * EARTH_RADIUS_METERS * Math.asin(Math.sqrt(sinHalf));
}
function coordinateCentroid(coordinates) {
	const total = coordinates.reduce((acc, coordinate) => ({
		lng: acc.lng + coordinate.lng,
		lat: acc.lat + coordinate.lat
	}), {
		lng: 0,
		lat: 0
	});
	return {
		lng: total.lng / coordinates.length,
		lat: total.lat / coordinates.length
	};
}
/** Wrap multiple popups into a vertical list. Single popup is returned as-is. */
function combinePopups(popups) {
	if (popups.length === 0) return void 0;
	if (popups.length === 1) return popups[0];
	return createElement("div", { style: {
		display: "flex",
		flexDirection: "column",
		backgroundColor: "transparent",
		overflow: "hidden"
	} }, popups.map((popup, index) => createElement("div", {
		key: index,
		style: index > 0 ? { borderTop: "1px solid #e5e7eb" } : void 0
	}, popup)));
}
function mergePointGroup(group) {
	if (group.length === 1) return group[0];
	const popups = group.map((point) => point.popup).filter(isRenderablePopup);
	const ids = group.map((point) => point.id).filter((id) => id != null);
	return {
		...group[0],
		id: ids.length > 0 ? ids.join("+") : void 0,
		coordinate: coordinateCentroid(group.map((point) => point.coordinate)),
		popup: combinePopups(popups)
	};
}
/**
* Groups points within `thresholdMeters` (transitively) and returns one point per group.
* Merged points share a centroid coordinate and a popup list.
*/
function mergeNearbyPoints(points, thresholdMeters) {
	if (points.length <= 1 || thresholdMeters <= 0) return points;
	const parent = points.map((_, index) => index);
	const find = (index) => {
		let root = index;
		while (parent[root] !== root) {
			const next = parent[root];
			parent[root] = parent[next];
			root = next;
		}
		return root;
	};
	const union = (a, b) => {
		const rootA = find(a);
		const rootB = find(b);
		if (rootA !== rootB) parent[rootB] = rootA;
	};
	for (let i = 0; i < points.length; i += 1) {
		const pointA = points[i];
		if (!pointA) continue;
		for (let j = i + 1; j < points.length; j += 1) {
			const pointB = points[j];
			if (!pointB) continue;
			if (distanceMeters(pointA.coordinate, pointB.coordinate) <= thresholdMeters) union(i, j);
		}
	}
	const groups = /* @__PURE__ */ new Map();
	for (let index = 0; index < points.length; index += 1) {
		const point = points[index];
		if (!point) continue;
		const root = find(index);
		const group = groups.get(root);
		if (group) group.push(point);
		else groups.set(root, [point]);
	}
	return [...groups.values()].map(mergePointGroup);
}
function resolveMergeNearbyMeters(options) {
	if (options === false) return null;
	return options ?? 10;
}
//#endregion
//#region src/components/Map/entities/Point/point.ts
var POINTS_SOURCE_ID = "maplib-points";
var POINTS_LAYER_ID = "maplib-points-circle";
var CLUSTER_CIRCLE_LAYER_ID = "maplib-points-cluster-circle";
var CLUSTER_COUNT_LAYER_ID = "maplib-points-cluster-count";
/** Stroke applied to the GeoJSON circle layer when world-copy mode is active. */
var WORLD_COPY_CIRCLE_STROKE_COLOR = "#fff";
var WORLD_COPY_CIRCLE_STROKE_WIDTH = 1.5;
function resolveClusterOptions(opt) {
	if (!opt) return null;
	if (opt === true) return {};
	return opt;
}
var DEFAULT_POINT_POPUP_OPTIONS = {
	closeButton: false,
	maxWidth: "none",
	anchor: "bottom",
	offset: [0, -20],
	className: "maplib-point-popup"
};
var Point = class {
	map;
	defaults;
	/** DOM markers for normal (non-cluster) mode. */
	renderedPoints = /* @__PURE__ */ new Map();
	/** DOM markers for individual unclustered points in cluster mode. */
	clusterDomMarkers = /* @__PURE__ */ new Map();
	/** key → RenderPointType, populated when cluster mode is active. */
	clusterPointsMap = /* @__PURE__ */ new Map();
	/**
	* key → popup ReactNode for GeoJSON circle points in world-copy mode.
	* These cannot use DOM-Marker popups because MapLibre repositions markers to
	* the nearest world copy; instead, clicks on the GeoJSON circle layer trigger
	* popups at the exact clicked coordinate (which may be any world copy).
	*/
	worldCopiesPopups = /* @__PURE__ */ new Map();
	/** Currently open popup in world-copy circle-click mode. */
	circlePopup = null;
	/** React root for the currently open circle popup. */
	circlePopupRoot = null;
	isClustered = false;
	clusterClickHandler = null;
	/** Click handler attached to the plain circle layer for world-copy popup support. */
	circleClickHandler = null;
	/** Feature id currently carrying the `hover` feature-state (drives the scale-up radius). */
	hoveredCircleFeatureId = null;
	constructor(map, defaults) {
		this.map = map;
		this.defaults = defaults;
	}
	setDefaults(defaults) {
		this.defaults = defaults;
	}
	render(points, options) {
		const mergeNearbyMeters = resolveMergeNearbyMeters(options?.mergeNearby);
		const resolvedPoints = mergeNearbyMeters == null ? points : mergeNearbyPoints(points, mergeNearbyMeters);
		const worldCopies = options?.renderWorldCopies === true;
		const wantedKeys = /* @__PURE__ */ new Set();
		this.worldCopiesPopups.clear();
		const clusterOpts = resolveClusterOptions(options?.cluster);
		const pointsForCircleLayer = [];
		if (clusterOpts !== null) this.clusterPointsMap.clear();
		for (let index = 0; index < resolvedPoints.length; index += 1) {
			const point = resolvedPoints[index];
			if (!point) continue;
			const key = pointStableKey(point, index);
			if (hasCircleMarker(point.marker)) {
				wantedKeys.add(key);
				if (isRenderablePopup(point.popup)) this.worldCopiesPopups.set(key, point.popup);
				pointsForCircleLayer.push(point);
				continue;
			}
			if (clusterOpts !== null) {
				wantedKeys.add(key);
				this.clusterPointsMap.set(key, point);
				pointsForCircleLayer.push(point);
				continue;
			}
			const marker = resolveEffectiveMarker(point);
			const hasExplicitMarker = point.marker != null && point.marker !== false;
			if (hasIconMarker(marker)) {
				wantedKeys.add(key);
				if (!this.renderedPoints.has(key)) this.renderedPoints.set(key, this.createIconMount(marker, point.coordinate, point.popup));
			} else if (isRenderableReactMarker(marker) && hasExplicitMarker) {
				wantedKeys.add(key);
				if (!this.renderedPoints.has(key)) this.renderedPoints.set(key, this.createReactMount(marker, point.coordinate, point.popup));
			} else if (isRenderableReactMarker(marker) && worldCopies) {
				wantedKeys.add(key);
				if (isRenderablePopup(point.popup)) this.worldCopiesPopups.set(key, point.popup);
				pointsForCircleLayer.push(point);
			} else if (isRenderableReactMarker(marker)) {
				wantedKeys.add(key);
				if (!this.renderedPoints.has(key)) this.renderedPoints.set(key, this.createReactMount(marker, point.coordinate, point.popup));
			} else {
				wantedKeys.add(key);
				if (isRenderablePopup(point.popup) && !this.renderedPoints.has(key)) if (worldCopies) this.worldCopiesPopups.set(key, point.popup);
				else this.renderedPoints.set(key, this.createStandaloneMount(point.popup, point.coordinate));
				pointsForCircleLayer.push(point);
			}
		}
		for (const [key, mount] of [...this.renderedPoints]) if (!wantedKeys.has(key)) {
			this.disposeMount(mount);
			this.renderedPoints.delete(key);
		}
		this.syncCircleLayer(pointsForCircleLayer, clusterOpts);
	}
	remove() {
		this.clearRenderedSync();
		this.clearClusterDomMarkers();
		this.removeCircleLayers();
		this.worldCopiesPopups.clear();
		this.closeCirclePopup();
	}
	popupContentHost() {
		const container = document.createElement("div");
		container.style.display = "contents";
		return container;
	}
	bindPopupToMarker(marker, popupContent) {
		const container = this.popupContentHost();
		const popupRoot = createRoot(container);
		popupRoot.render(popupContent);
		marker.setPopup(new Popup(DEFAULT_POINT_POPUP_OPTIONS).setDOMContent(container));
		return popupRoot;
	}
	createIconMount(icon, coordinate, popup) {
		const img = document.createElement("img");
		img.src = icon.src;
		img.alt = icon.alt ?? "";
		img.decoding = "async";
		img.draggable = false;
		const w = icon.width ?? this.defaults.markerSize;
		const h = icon.height ?? this.defaults.markerSize;
		img.style.width = `${w}px`;
		img.style.height = `${h}px`;
		img.style.display = "block";
		const mapMarker = new Marker({
			element: img,
			anchor: "center"
		}).setLngLat(lngLatTuple(coordinate)).addTo(this.map);
		const popupRoot = isRenderablePopup(popup) ? this.bindPopupToMarker(mapMarker, popup) : void 0;
		return popupRoot ? {
			kind: "icon",
			mapMarker,
			popupRoot
		} : {
			kind: "icon",
			mapMarker
		};
	}
	createReactMount(node, coordinate, popup) {
		const element = document.createElement("div");
		const root = createRoot(element);
		root.render(node);
		const mapMarker = new Marker({
			element,
			anchor: "center"
		}).setLngLat(lngLatTuple(coordinate)).addTo(this.map);
		const popupRoot = isRenderablePopup(popup) ? this.bindPopupToMarker(mapMarker, popup) : void 0;
		return popupRoot ? {
			kind: "react",
			mapMarker,
			root,
			popupRoot
		} : {
			kind: "react",
			mapMarker,
			root
		};
	}
	createStandaloneMount(content, coordinate) {
		const container = this.popupContentHost();
		const root = createRoot(container);
		root.render(content);
		return {
			kind: "standalone",
			mapPopup: new Popup(DEFAULT_POINT_POPUP_OPTIONS).setDOMContent(container).setLngLat(lngLatTuple(coordinate)).addTo(this.map),
			root
		};
	}
	disposeMount(m) {
		switch (m.kind) {
			case "icon": {
				const popupRoot = m.popupRoot;
				if (popupRoot) queueMicrotask(() => popupRoot.unmount());
				m.mapMarker.getPopup()?.remove();
				m.mapMarker.remove();
				break;
			}
			case "react": {
				const { root, popupRoot } = m;
				if (popupRoot) queueMicrotask(() => popupRoot.unmount());
				m.mapMarker.getPopup()?.remove();
				m.mapMarker.remove();
				queueMicrotask(() => root.unmount());
				break;
			}
			case "standalone": {
				const { root } = m;
				m.mapPopup.remove();
				queueMicrotask(() => root.unmount());
				break;
			}
		}
	}
	clearRenderedSync() {
		for (const mount of this.renderedPoints.values()) this.disposeMount(mount);
		this.renderedPoints.clear();
	}
	clearClusterDomMarkers() {
		for (const mount of this.clusterDomMarkers.values()) this.disposeMount(mount);
		this.clusterDomMarkers.clear();
	}
	removeCircleLayers() {
		this.map.off("idle", this.onMapIdle);
		if (this.clusterClickHandler) {
			this.map.off("click", CLUSTER_CIRCLE_LAYER_ID, this.clusterClickHandler);
			this.clusterClickHandler = null;
		}
		this.map.off("mouseenter", CLUSTER_CIRCLE_LAYER_ID, this.onClusterMouseEnter);
		this.map.off("mouseleave", CLUSTER_CIRCLE_LAYER_ID, this.onClusterMouseLeave);
		if (this.circleClickHandler) {
			this.map.off("click", POINTS_LAYER_ID, this.circleClickHandler);
			this.circleClickHandler = null;
		}
		this.map.off("mouseenter", POINTS_LAYER_ID, this.onCircleMouseEnter);
		this.map.off("mouseleave", POINTS_LAYER_ID, this.onCircleMouseLeave);
		this.map.off("mousemove", POINTS_LAYER_ID, this.onCircleHoverMove);
		this.map.off("mouseleave", POINTS_LAYER_ID, this.onCircleHoverLeave);
		this.hoveredCircleFeatureId = null;
		for (const id of [
			CLUSTER_COUNT_LAYER_ID,
			CLUSTER_CIRCLE_LAYER_ID,
			POINTS_LAYER_ID
		]) if (this.map.getLayer(id)) this.map.removeLayer(id);
		if (this.map.getSource(POINTS_SOURCE_ID)) this.map.removeSource(POINTS_SOURCE_ID);
		this.isClustered = false;
	}
	onClusterMouseEnter = () => {
		this.map.getCanvas().style.cursor = "pointer";
	};
	onClusterMouseLeave = () => {
		this.map.getCanvas().style.cursor = "";
	};
	/** Fires when the map finishes all animations and tile loading. */
	onMapIdle = () => {
		if (this.isClustered) this.syncClusterDomMarkers();
	};
	/**
	* Queries the visible unclustered features from the GeoJSON source and reconciles
	* DOM markers so each visible individual point has its custom React/icon marker.
	*/
	syncClusterDomMarkers() {
		const features = this.map.querySourceFeatures(POINTS_SOURCE_ID, { filter: ["!", ["has", "point_count"]] });
		const visibleKeys = /* @__PURE__ */ new Set();
		for (const feature of features) {
			const rawKey = feature.properties?.pointKey;
			if (typeof rawKey === "string" && rawKey) visibleKeys.add(rawKey);
		}
		for (const [key, mount] of [...this.clusterDomMarkers]) if (!visibleKeys.has(key)) {
			this.disposeMount(mount);
			this.clusterDomMarkers.delete(key);
		}
		for (const key of visibleKeys) {
			if (this.clusterDomMarkers.has(key)) continue;
			const point = this.clusterPointsMap.get(key);
			if (!point) continue;
			const marker = resolveEffectiveMarker(point);
			if (hasIconMarker(marker)) this.clusterDomMarkers.set(key, this.createIconMount(marker, point.coordinate, point.popup));
			else if (isRenderableReactMarker(marker)) this.clusterDomMarkers.set(key, this.createReactMount(marker, point.coordinate, point.popup));
		}
	}
	syncCircleLayer(points, clusterOpts) {
		const wantsClustered = clusterOpts !== null;
		if (this.isClustered !== wantsClustered && this.map.getSource(POINTS_SOURCE_ID)) this.removeCircleLayers();
		const data = this.toFeatureCollection(points);
		const existing = this.map.getSource(POINTS_SOURCE_ID);
		if (existing instanceof GeoJSONSource) {
			this.onCircleHoverLeave();
			existing.setData(data);
			if (wantsClustered) this.clearClusterDomMarkers();
			return;
		}
		if (points.length === 0) return;
		if (clusterOpts !== null) this.addClusteredLayers(data, clusterOpts);
		else this.addPlainLayers(data);
	}
	/**
	* Adds the plain (non-clustered) GeoJSON circle layer.
	*
	* All style properties are driven by per-feature GeoJSON properties so that both
	* preset transport-type circles (world-copy mode) and explicit `PointCircleMarker`
	* objects can each carry their own color / radius / stroke / opacity.
	* Unset feature properties fall back to the map defaults.
	*
	* `circle-radius` grows by `circleScale` while the feature carries the `hover`
	* feature-state (set by `onCircleHoverMove` / `onCircleHoverLeave`).
	*/
	addPlainLayers(data) {
		this.map.addSource(POINTS_SOURCE_ID, {
			type: "geojson",
			data
		});
		const baseRadius = [
			"coalesce",
			["get", "circleRadius"],
			this.defaults.circleRadius
		];
		const hoverScale = [
			"coalesce",
			["get", "circleScale"],
			this.defaults.circleScale
		];
		const paint = {
			"circle-color": [
				"coalesce",
				["get", "circleColor"],
				this.defaults.circleColor
			],
			"circle-radius": [
				"case",
				[
					"boolean",
					["feature-state", "hover"],
					false
				],
				[
					"*",
					baseRadius,
					hoverScale
				],
				baseRadius
			],
			"circle-radius-transition": {
				duration: 150,
				delay: 0
			},
			"circle-stroke-color": [
				"coalesce",
				["get", "circleStrokeColor"],
				"rgba(0,0,0,0)"
			],
			"circle-stroke-width": [
				"coalesce",
				["get", "circleStrokeWidth"],
				0
			],
			"circle-opacity": [
				"coalesce",
				["get", "circleOpacity"],
				1
			]
		};
		this.map.addLayer({
			id: POINTS_LAYER_ID,
			type: "circle",
			source: POINTS_SOURCE_ID,
			paint
		});
		this.map.on("mousemove", POINTS_LAYER_ID, this.onCircleHoverMove);
		this.map.on("mouseleave", POINTS_LAYER_ID, this.onCircleHoverLeave);
		if (this.worldCopiesPopups.size > 0) {
			this.circleClickHandler = (e) => this.onCircleClick(e);
			this.map.on("click", POINTS_LAYER_ID, this.circleClickHandler);
			this.map.on("mouseenter", POINTS_LAYER_ID, this.onCircleMouseEnter);
			this.map.on("mouseleave", POINTS_LAYER_ID, this.onCircleMouseLeave);
		}
		this.isClustered = false;
	}
	onCircleHoverMove = (e) => {
		const feature = e.features?.[0];
		const featureId = typeof feature?.id === "number" ? feature.id : null;
		if (featureId === this.hoveredCircleFeatureId) return;
		if (this.hoveredCircleFeatureId !== null) this.map.setFeatureState({
			source: POINTS_SOURCE_ID,
			id: this.hoveredCircleFeatureId
		}, { hover: false });
		this.hoveredCircleFeatureId = featureId;
		if (featureId !== null) this.map.setFeatureState({
			source: POINTS_SOURCE_ID,
			id: featureId
		}, { hover: true });
	};
	onCircleHoverLeave = () => {
		if (this.hoveredCircleFeatureId !== null) {
			this.map.setFeatureState({
				source: POINTS_SOURCE_ID,
				id: this.hoveredCircleFeatureId
			}, { hover: false });
			this.hoveredCircleFeatureId = null;
		}
	};
	onCircleMouseEnter = () => {
		this.map.getCanvas().style.cursor = "pointer";
	};
	onCircleMouseLeave = () => {
		this.map.getCanvas().style.cursor = "";
	};
	onCircleClick(e) {
		if (e.originalEvent.target !== this.map.getCanvas()) return;
		const feature = e.features?.[0];
		if (!feature) return;
		const key = feature.properties?.pointKey;
		if (typeof key !== "string") return;
		const popupContent = this.worldCopiesPopups.get(key);
		if (!isRenderablePopup(popupContent)) return;
		this.closeCirclePopup();
		const geom = feature.geometry;
		const featureLng = (geom.type === "Point" ? geom.coordinates[0] : null) ?? e.lngLat.lng;
		const featureLat = (geom.type === "Point" ? geom.coordinates[1] : null) ?? e.lngLat.lat;
		const anchorLng = featureLng + Math.round((e.lngLat.lng - featureLng) / 360) * 360;
		const container = document.createElement("div");
		container.style.display = "contents";
		const root = createRoot(container);
		root.render(popupContent);
		this.circlePopup = new Popup(DEFAULT_POINT_POPUP_OPTIONS).setDOMContent(container).setLngLat([anchorLng, featureLat]).addTo(this.map);
		this.circlePopupRoot = root;
	}
	closeCirclePopup() {
		if (this.circlePopup) {
			this.circlePopup.remove();
			this.circlePopup = null;
		}
		if (this.circlePopupRoot) {
			const root = this.circlePopupRoot;
			this.circlePopupRoot = null;
			queueMicrotask(() => root.unmount());
		}
	}
	addClusteredLayers(data, opts) {
		const maxZoom = opts.maxZoom ?? this.defaults.clusterMaxZoom;
		const radius = opts.radius ?? this.defaults.clusterRadius;
		const circleColor = opts.circleColor ?? this.defaults.clusterCircleColor;
		const textColor = opts.textColor ?? this.defaults.clusterTextColor;
		this.map.addSource(POINTS_SOURCE_ID, {
			type: "geojson",
			data,
			cluster: true,
			clusterMaxZoom: maxZoom,
			clusterRadius: radius
		});
		this.map.addLayer({
			id: CLUSTER_CIRCLE_LAYER_ID,
			type: "circle",
			source: POINTS_SOURCE_ID,
			filter: ["has", "point_count"],
			paint: {
				"circle-color": circleColor,
				"circle-radius": [
					"step",
					["get", "point_count"],
					20,
					10,
					30,
					50,
					40
				],
				"circle-opacity": .85
			}
		});
		try {
			this.map.addLayer({
				id: CLUSTER_COUNT_LAYER_ID,
				type: "symbol",
				source: POINTS_SOURCE_ID,
				filter: ["has", "point_count"],
				layout: {
					"text-field": "{point_count_abbreviated}",
					"text-size": 13,
					"text-font": ["Open Sans Bold", "Arial Unicode MS Bold"]
				},
				paint: { "text-color": textColor }
			});
		} catch {}
		this.map.on("idle", this.onMapIdle);
		this.clusterClickHandler = async (e) => {
			if (e.originalEvent.target !== this.map.getCanvas()) return;
			if (!e.features?.length) return;
			const feature = e.features[0];
			const rawId = feature.properties?.cluster_id;
			if (typeof rawId !== "number") return;
			const source = this.map.getSource(POINTS_SOURCE_ID);
			if (!(source instanceof GeoJSONSource)) return;
			if (feature.geometry.type !== "Point") return;
			const coords = feature.geometry.coordinates;
			const zoom = await source.getClusterExpansionZoom(rawId);
			this.map.easeTo({
				center: [coords[0] ?? 0, coords[1] ?? 0],
				zoom
			});
		};
		this.map.on("click", CLUSTER_CIRCLE_LAYER_ID, this.clusterClickHandler);
		this.map.on("mouseenter", CLUSTER_CIRCLE_LAYER_ID, this.onClusterMouseEnter);
		this.map.on("mouseleave", CLUSTER_CIRCLE_LAYER_ID, this.onClusterMouseLeave);
		this.isClustered = true;
	}
	toFeatureCollection(points) {
		return {
			type: "FeatureCollection",
			features: points.map((point, index) => {
				let circleColor;
				let circleRadius;
				let circleStrokeColor;
				let circleStrokeWidth;
				let circleOpacity;
				let circleScale;
				if (hasCircleMarker(point.marker)) ({color: circleColor, radius: circleRadius, strokeColor: circleStrokeColor, strokeWidth: circleStrokeWidth, opacity: circleOpacity, scale: circleScale} = point.marker);
				else if (point.transportType != null) {
					circleColor = transportPointCircleColor[point.transportType];
					circleStrokeColor = WORLD_COPY_CIRCLE_STROKE_COLOR;
					circleStrokeWidth = WORLD_COPY_CIRCLE_STROKE_WIDTH;
				}
				return {
					type: "Feature",
					id: index,
					geometry: {
						type: "Point",
						coordinates: lngLatTuple(point.coordinate)
					},
					properties: {
						pointKey: pointStableKey(point, index),
						...circleColor != null && { circleColor },
						...circleRadius != null && { circleRadius },
						...circleStrokeColor != null && { circleStrokeColor },
						...circleStrokeWidth != null && { circleStrokeWidth },
						...circleOpacity != null && { circleOpacity },
						...circleScale != null && { circleScale }
					}
				};
			})
		};
	}
};
//#endregion
//#region src/components/Map/entities/Route/route.utils.ts
/** Preset key for line (and point) styles: `transportType` or sea when empty. */
function transportPresetKeyFromSegment(segment) {
	return segment.transportType ?? RouteTransportTypes.SEA;
}
//#endregion
//#region src/components/Map/entities/Route/route.ts
var ROUTE_SOURCE_ID = "maplib-routes";
var ROUTE_LAYER_SOLID_ID = "maplib-routes-line-solid";
var ROUTE_LAYER_DASHED_ID = "maplib-routes-line-dashed";
/** Same cap as legacy pin search: ignore matches farther than this (degrees, Chebyshev). */
var MAX_PIN_MATCH_MARGIN = 10;
var FILTER_SOLID = [
	"any",
	["!", ["has", "lineType"]],
	[
		"==",
		["get", "lineType"],
		"solid"
	]
];
var FILTER_DASHED = [
	"==",
	["get", "lineType"],
	"dashed"
];
var Route = class {
	map;
	defaults;
	renderedRoutes = [];
	constructor(map, defaults) {
		this.map = map;
		this.defaults = defaults;
	}
	setDefaults(defaults) {
		this.defaults = defaults;
		this.syncPaintFromDefaults();
	}
	syncPaintFromDefaults() {
		this.applyLinePaint();
	}
	render(config) {
		const segments = this.getFullRoute(config);
		const data = this.toFeatureCollection(segments);
		const existing = this.map.getSource(ROUTE_SOURCE_ID);
		this.renderedRoutes = config.route;
		if (existing instanceof GeoJSONSource) {
			existing.setData(data);
			this.applyLinePaint();
			return config;
		}
		if (data.features.length === 0) return config;
		this.map.addSource(ROUTE_SOURCE_ID, {
			type: "geojson",
			data
		});
		const commonLayout = {
			"line-join": "round",
			"line-cap": "round"
		};
		this.map.addLayer({
			id: ROUTE_LAYER_SOLID_ID,
			type: "line",
			source: ROUTE_SOURCE_ID,
			filter: FILTER_SOLID,
			layout: commonLayout,
			paint: {
				"line-color": ["get", "lineColor"],
				"line-width": ["get", "lineWidth"],
				"line-opacity": ["get", "lineOpacity"]
			}
		});
		this.map.addLayer({
			id: ROUTE_LAYER_DASHED_ID,
			type: "line",
			source: ROUTE_SOURCE_ID,
			filter: FILTER_DASHED,
			layout: commonLayout,
			paint: {
				"line-color": ["get", "lineColor"],
				"line-width": ["get", "lineWidth"],
				"line-opacity": ["get", "lineOpacity"],
				"line-dasharray": [...this.defaults.lineDashArray]
			}
		});
		return config;
	}
	flyToRoute(options) {
		const rawCoords = this.renderedRoutes.flatMap((s) => s.coordinates);
		fitMapToCoordinates(this.map, rawCoords, options);
	}
	remove() {
		this.renderedRoutes = [];
		for (const id of [ROUTE_LAYER_DASHED_ID, ROUTE_LAYER_SOLID_ID]) if (this.map.getLayer(id)) this.map.removeLayer(id);
		if (this.map.getSource(ROUTE_SOURCE_ID)) this.map.removeSource(ROUTE_SOURCE_ID);
	}
	applyLinePaint() {
		if (this.map.getLayer(ROUTE_LAYER_DASHED_ID)) this.map.setPaintProperty(ROUTE_LAYER_DASHED_ID, "line-dasharray", [...this.defaults.lineDashArray]);
	}
	toFeatureCollection(segments) {
		const features = [];
		for (const segment of segments) {
			if (segment.coordinates.length < 2) continue;
			const lineType = segment.lineType ?? "solid";
			const coordinates = segment.coordinates.map((c) => lngLatTuple(c));
			const presetColor = linePresetMap[transportPresetKeyFromSegment(segment)];
			const cfg = segment.renderLineConfig;
			const properties = {
				lineType,
				lineColor: cfg?.colorLine ?? presetColor,
				lineWidth: cfg?.widthLine ?? this.defaults.lineWidth,
				lineOpacity: cfg?.opacityLine ?? this.defaults.lineOpacity,
				...segment.transportType !== void 0 ? { transportType: segment.transportType } : {}
			};
			features.push({
				type: "Feature",
				properties,
				geometry: {
					type: "LineString",
					coordinates
				}
			});
		}
		return {
			type: "FeatureCollection",
			features
		};
	}
	getFullRoute(renderRouteData) {
		if (!renderRouteData.route.length) return [];
		const currentPinIndexInSegments = renderRouteData.currentPin != null ? this.findCurrentPinInRoute(renderRouteData.currentPin, renderRouteData.route) : {
			segmentIndex: -1,
			pointIndex: -1
		};
		const fullRouteCoords = createAntimeridianFixedPoints(this.mergeCoordinates(renderRouteData.route));
		const routeChunks = renderRouteData.route.map((segment) => ({
			...segment,
			coordinates: createAntimeridianFixedPoints(segment.coordinates)
		}));
		const route = this.getFullRouteSlicePerChunk(routeChunks, fullRouteCoords);
		if (!route.length) return [];
		if (!(currentPinIndexInSegments.segmentIndex !== -1 && currentPinIndexInSegments.pointIndex !== -1)) return this.applyDefaultLineKinds(route);
		const target = route[currentPinIndexInSegments.segmentIndex];
		if (!target) return this.applyDefaultLineKinds(route);
		const { pointIndex } = currentPinIndexInSegments;
		const sliced = [{
			...target,
			coordinates: target.coordinates.slice(0, pointIndex + 1)
		}, {
			...target,
			coordinates: target.coordinates.slice(pointIndex)
		}];
		const withSplit = [...route];
		withSplit.splice(currentPinIndexInSegments.segmentIndex, 1, ...sliced);
		return withSplit.map((segment, index) => {
			if (segment.lineType != null) return segment;
			const dashed = index > currentPinIndexInSegments.segmentIndex;
			return {
				...segment,
				lineType: dashed ? "dashed" : "solid"
			};
		});
	}
	/**
	* Replaces each chunk's `coordinates` with the corresponding slice of the merged,
	* antimeridian-adjusted polyline (`fullRoute`).
	*/
	getFullRouteSlicePerChunk(routeChunks, fullRoute) {
		let cursor = 0;
		return routeChunks.map((chunk) => {
			const len = chunk.coordinates.length;
			const start = cursor;
			cursor += len;
			return {
				...chunk,
				coordinates: fullRoute.slice(start, cursor)
			};
		});
	}
	mergeCoordinates(routeSegments) {
		if (!routeSegments.length) return [];
		return routeSegments.flatMap((s) => s.coordinates);
	}
	/** Keeps explicit `lineType` on segments; defaults the rest to `solid`. */
	applyDefaultLineKinds(segments) {
		return segments.map((segment) => ({
			...segment,
			lineType: segment.lineType ?? "solid"
		}));
	}
	findCurrentPinInRoute(currentPin, routeSegments) {
		let best = null;
		for (let segmentIndex = 0; segmentIndex < routeSegments.length; segmentIndex += 1) {
			const segment = routeSegments[segmentIndex];
			if (!segment) continue;
			const coords = segment.coordinates;
			for (let pointIndex = 0; pointIndex < coords.length; pointIndex += 1) {
				const point = coords[pointIndex];
				if (!point) continue;
				const threshold = Math.max(Math.abs(point.lat - currentPin.lat), Math.abs(point.lng - currentPin.lng));
				if (best === null || threshold < best.threshold) best = {
					lat: point.lat,
					lng: point.lng,
					threshold,
					segmentIndex,
					pointIndex
				};
			}
		}
		if (best === null || best.threshold > MAX_PIN_MATCH_MARGIN) return {
			segmentIndex: -1,
			pointIndex: -1
		};
		const matched = best;
		const isLastPoint = matched.segmentIndex === routeSegments.length - 1 && matched.pointIndex === routeSegments[matched.segmentIndex].coordinates.length - 1;
		return {
			segmentIndex: matched.segmentIndex,
			pointIndex: isLastPoint ? -1 : matched.pointIndex
		};
	}
};
//#endregion
//#region src/components/Map/mapEngine.ts
var MapEngine = class {
	map;
	points;
	routes;
	originCenter;
	originZoom;
	fitBoundsPadding;
	activeStyle;
	lastRouteConfig = null;
	lastPointsConfig = null;
	globeIdleZoom;
	constructor(options) {
		const opts = defineDefaultOptions(options);
		this.map = new Map$2({
			...opts,
			attributionControl: false
		});
		this.points = new Point(this.map, opts.pointDefaults);
		this.routes = new Route(this.map, opts.routeDefaults);
		this.originCenter = opts.center;
		this.originZoom = opts.zoom;
		this.fitBoundsPadding = opts.fitBoundsPadding;
		this.activeStyle = opts.style;
		this.globeIdleZoom = opts.globeControl?.zoom ?? 2.2;
		this.map.setPadding(toPaddingOptions(this.fitBoundsPadding));
		this.syncProjectionAttr();
	}
	setMapProjection(type) {
		this.map.setProjection({ type });
		this.syncProjectionAttr();
	}
	syncProjectionAttr() {
		const isGlobe = this.map.getProjection?.()?.type === "globe";
		this.map.getContainer().dataset.projection = isGlobe ? "globe" : "mercator";
	}
	/** Applies the latest resolved init options (called by `Map` on every relevant change). */
	applyInitOptions(options) {
		this.fitBoundsPadding = options.fitBoundsPadding;
		this.originCenter = options.center;
		this.originZoom = options.zoom;
		this.globeIdleZoom = options.globeControl?.zoom ?? 2.2;
		this.points.setDefaults(options.pointDefaults);
		this.routes.setDefaults(options.routeDefaults);
		this.syncPadding();
		if (options.style !== this.activeStyle) {
			const keepGlobe = this.map.getProjection?.()?.type === "globe";
			this.activeStyle = options.style;
			this.map.setStyle(options.style);
			this.map.once("style.load", () => {
				this.syncPadding();
				if (keepGlobe) this.setMapProjection("globe");
				else this.syncProjectionAttr();
				this.restoreRenderedContent();
			});
			return;
		}
		this.routes.syncPaintFromDefaults();
	}
	renderPoints(points, options) {
		this.lastPointsConfig = {
			points,
			options
		};
		this.points.render(points, this.resolvePointsOptions(options));
	}
	unmountPoints() {
		this.lastPointsConfig = null;
		this.points.remove();
	}
	renderRoute(config) {
		this.lastRouteConfig = config;
		const result = this.routes.render(config);
		if (this.lastPointsConfig) {
			const { points, options } = this.lastPointsConfig;
			this.points.render(points, this.resolvePointsOptions(options));
		}
		return result;
	}
	unmountRoute() {
		this.lastRouteConfig = null;
		this.routes.remove();
	}
	/**
	* Merges caller-supplied point options with internally-managed flags.
	* When a route is active, `renderWorldCopies` is forced to `true` so that
	* DOM markers appear in every MapLibre world copy, matching the route line.
	*/
	resolvePointsOptions(options) {
		if (this.lastRouteConfig == null) return options;
		return {
			...options,
			renderWorldCopies: true
		};
	}
	flyToBounds(options) {
		this.routes.flyToRoute({
			padding: 40,
			...options
		});
	}
	/** Same as `flyToBounds`. Name used by the tracking and transit-time apps. */
	flyToRoute(options) {
		this.flyToBounds(options);
	}
	hasRenderedRoute() {
		return Boolean(this.lastRouteConfig?.route.length);
	}
	/** Fits the map to an arbitrary set of coordinates, unwrapping across the antimeridian. */
	flyToCoordinates(coordinates, options) {
		fitMapToCoordinates(this.map, coordinates, {
			padding: 40,
			...options
		});
	}
	flyToOrigin() {
		const isGlobe = this.map.getProjection?.()?.type === "globe";
		this.map.flyTo({
			center: this.originCenter,
			zoom: isGlobe ? this.globeIdleZoom : this.originZoom,
			essential: true
		});
	}
	/** Applies `fitBoundsPadding` as persistent camera padding, skipping the call when unchanged. */
	syncPadding() {
		const next = toPaddingOptions(this.fitBoundsPadding);
		const current = this.map.getPadding();
		if (current.top === next.top && current.right === next.right && current.bottom === next.bottom && current.left === next.left) return;
		this.map.setPadding(next);
	}
	restoreRenderedContent() {
		if (this.lastRouteConfig) this.routes.render(this.lastRouteConfig);
		if (this.lastPointsConfig) {
			const { points, options } = this.lastPointsConfig;
			this.points.render(points, this.resolvePointsOptions(options));
		}
	}
};
var Map_module_default = {
	map: "map--Map--Zj4",
	mapContainer: "mapContainer--Map--71z"
};
//#endregion
//#region src/components/Map/Map.tsx
var _jsxFileName$1 = "/home/forge/apps.dev.qubictron.com/packages/maplib/src/components/Map/Map.tsx";
var Map$1 = ({ getMap, initOptions }) => {
	const mapContainerRef = useRef(null);
	const [mapEngine, setMapEngine] = useState(null);
	const resolvedInitOptions = resolveInitOptions(initOptions);
	const initOptionsSyncDeps = getInitOptionsSyncDeps(initOptions);
	useLayoutEffect(() => {
		if (!mapContainerRef.current) return;
		const mapEngine = new MapEngine({
			...initOptions,
			container: mapContainerRef.current
		});
		getMap(mapEngine);
		setMapEngine(mapEngine);
		return () => {
			mapEngine.map.remove();
			setMapEngine(null);
		};
	}, []);
	useEffect(() => {
		mapEngine?.applyInitOptions(resolveInitOptions(initOptions));
	}, [mapEngine, ...initOptionsSyncDeps]);
	useEffect(() => {
		if (!mapEngine || !mapContainerRef.current) return;
		const map = mapEngine.map;
		const resize = () => {
			map.resize();
		};
		if (map.loaded()) resize();
		else map.once("load", resize);
		return () => {
			map.off("load", resize);
		};
	}, [mapEngine]);
	return /* @__PURE__ */ jsxDEV("div", {
		ref: mapContainerRef,
		className: Map_module_default.map,
		"data-theme": resolvedInitOptions.theme,
		children: mapEngine && /* @__PURE__ */ jsxDEV(MapProvider, {
			value: {
				engine: mapEngine,
				initOptions: resolvedInitOptions,
				mapContainerRef
			},
			children: /* @__PURE__ */ jsxDEV(MapCore, {}, void 0, false, {
				fileName: _jsxFileName$1,
				lineNumber: 65,
				columnNumber: 11
			}, void 0)
		}, void 0, false, {
			fileName: _jsxFileName$1,
			lineNumber: 64,
			columnNumber: 9
		}, void 0)
	}, void 0, false, {
		fileName: _jsxFileName$1,
		lineNumber: 62,
		columnNumber: 5
	}, void 0);
};
var Popup_module_default = {
	popup: "popup--Popup--qu4",
	clickable: "clickable--Popup--zeS",
	pointPopup: "pointPopup--Popup--uJR",
	routePopup: "routePopup--Popup--z42",
	pill: "pill--Popup--KTQ",
	reference: "reference--Popup--WMD",
	count: "count--Popup--8cr",
	route: "route--Popup--Pzy"
};
//#endregion
//#region src/components/Presets/Popup/Popup.tsx
var _jsxFileName = "/home/forge/apps.dev.qubictron.com/packages/maplib/src/components/Presets/Popup/Popup.tsx";
var shipmentColorMap = {
	[RouteTransportTypes.SEA]: seaColorLine,
	[RouteTransportTypes.AIR]: airColorLine,
	[RouteTransportTypes.ROAD]: roadColorLine,
	[RouteTransportTypes.RAIL]: railColorLine,
	[RouteTransportTypes.OTHER]: otherColorLine
};
var shipmentPillBgMap = {
	[RouteTransportTypes.SEA]: "rgba(0, 136, 255, 0.15)",
	[RouteTransportTypes.AIR]: "rgba(245, 44, 92, 0.15)",
	[RouteTransportTypes.ROAD]: "rgba(2, 188, 177, 0.15)",
	[RouteTransportTypes.RAIL]: "rgba(255, 168, 0, 0.15)",
	[RouteTransportTypes.OTHER]: "rgba(233, 179, 107, 0.15)"
};
/** Single-location bubble (e.g. "Yantian, CN"). Single centered tail, Shadow 2. */
var PointPopup = ({ label, shipmentType, onClick }) => {
	const color = shipmentType ? shipmentColorMap[shipmentType] : seaColorLine;
	return /* @__PURE__ */ jsxDEV("div", {
		className: (0, import_classnames.default)(Popup_module_default.popup, Popup_module_default.pointPopup, { [Popup_module_default.clickable]: !!onClick }),
		style: { "--popup-color": color },
		onClick,
		children: label
	}, void 0, false, {
		fileName: _jsxFileName,
		lineNumber: 42,
		columnNumber: 5
	}, void 0);
};
/** Route bubble with optional pill (reference + count) and From → To row. Double tail, Shadow 1. */
var RoutePopup = ({ from, to, reference, count, shipmentType, onClick }) => {
	const color = shipmentType ? shipmentColorMap[shipmentType] : seaColorLine;
	const pillBg = shipmentPillBgMap[shipmentType ?? RouteTransportTypes.SEA];
	const hasPill = reference || count !== void 0;
	const hasRoute = from || to;
	if (!hasPill && !hasRoute) return null;
	return /* @__PURE__ */ jsxDEV("div", {
		className: (0, import_classnames.default)(Popup_module_default.popup, Popup_module_default.routePopup, { [Popup_module_default.clickable]: !!onClick }),
		style: { "--popup-color": color },
		onClick,
		children: [hasPill && /* @__PURE__ */ jsxDEV("div", {
			className: Popup_module_default.pill,
			style: { background: pillBg },
			children: [/* @__PURE__ */ jsxDEV("span", {
				className: Popup_module_default.reference,
				children: reference
			}, void 0, false, {
				fileName: _jsxFileName,
				lineNumber: 82,
				columnNumber: 11
			}, void 0), count !== void 0 && /* @__PURE__ */ jsxDEV("span", {
				className: Popup_module_default.count,
				children: count
			}, void 0, false, {
				fileName: _jsxFileName,
				lineNumber: 83,
				columnNumber: 35
			}, void 0)]
		}, void 0, true, {
			fileName: _jsxFileName,
			lineNumber: 81,
			columnNumber: 9
		}, void 0), hasRoute && /* @__PURE__ */ jsxDEV("div", {
			className: Popup_module_default.route,
			children: [/* @__PURE__ */ jsxDEV("span", { children: from }, void 0, false, {
				fileName: _jsxFileName,
				lineNumber: 89,
				columnNumber: 11
			}, void 0), to && /* @__PURE__ */ jsxDEV(Fragment, { children: [/* @__PURE__ */ jsxDEV(ChevronRight, {}, void 0, false, {
				fileName: _jsxFileName,
				lineNumber: 92,
				columnNumber: 15
			}, void 0), /* @__PURE__ */ jsxDEV("span", { children: to }, void 0, false, {
				fileName: _jsxFileName,
				lineNumber: 93,
				columnNumber: 15
			}, void 0)] }, void 0, true, {
				fileName: _jsxFileName,
				lineNumber: 91,
				columnNumber: 13
			}, void 0)]
		}, void 0, true, {
			fileName: _jsxFileName,
			lineNumber: 88,
			columnNumber: 9
		}, void 0)]
	}, void 0, true, {
		fileName: _jsxFileName,
		lineNumber: 75,
		columnNumber: 5
	}, void 0);
};
//#endregion
export { AirPoint, BlueCircleIcon, ChevronRight, GreenCircleIcon, Map$1 as Map, MapEngine, MapTheme, OrangeCircleIcon, OtherCircleIcon, OtherPoint, PointPopup, RailPoint, RedCircleIcon, RoadPoint, RoutePopup, RouteTransportTypes, SeaPoint, airColorLine, linePresetMap, otherColorLine, pointPresetMap, railColorLine, roadColorLine, seaColorLine };

//# sourceMappingURL=index.es.js.map